import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { useParams2 } from "../contexts/ParamsContext";

const CAT_LABELS = {
  actualite: "Actualité", conseil_municipal: "Conseil municipal",
  arrete: "Arrêté", manifestation: "Manifestation",
  vie_scolaire: "Vie scolaire", information: "Information",
};

const PUB = (typeof process !== "undefined" && process.env && process.env.PUBLIC_URL) || "";
const UPLOADS = "/uploads/actualites/";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "numeric", month: "long", year: "numeric",
    });
  } catch { return iso; }
}

/* ============================================================
   Hooks d'animation
   ============================================================ */

/** Révèle au scroll tous les éléments [data-reveal] (une seule fois). */
function useScrollReveal(deps = []) {
  useEffect(() => {
    const els = document.querySelectorAll("[data-reveal]:not(.revealed)");
    if (!els.length) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { els.forEach((el) => el.classList.add("revealed")); return; }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("revealed");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/** Parallax multicouche du hero (photo + silhouette), via rAF. */
function useHeroParallax(bgRef, silRef) {
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const y = window.scrollY;
        if (y > 900) return; // hero hors champ : on ne calcule plus
        if (bgRef.current) bgRef.current.style.transform = `translate3d(0, ${y * 0.32}px, 0)`;
        if (silRef.current) silRef.current.style.transform = `translate3d(0, ${y * 0.14}px, 0)`;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [bgRef, silRef]);
}

/** Compteur animé qui se déclenche à l'apparition. */
function Counter({ to, suffix = "", duration = 1800 }) {
  const ref = useRef(null);
  const [val, setVal] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const target = Number(to) || 0;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        if (reduce) { setVal(target); return; }
        const t0 = performance.now();
        const tick = (t) => {
          const p = Math.min(1, (t - t0) / duration);
          const eased = 1 - Math.pow(1 - p, 3); // ease-out cubic
          setVal(Math.round(target * eased));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      },
      { threshold: 0.5 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [to, duration]);

  return <span ref={ref}>{val.toLocaleString("fr-FR")}{suffix}</span>;
}

/* ============================================================
   Icônes raccourcis
   ============================================================ */
const ICONS = {
  actualites: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16v16H4z M4 9h16 M9 4v16"/>
    </svg>
  ),
  calendrier: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="16" rx="2"/>
      <path d="M3 10h18 M8 3v4 M16 3v4"/>
    </svg>
  ),
  demarches: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z M14 3v6h6 M8 13h8 M8 17h6"/>
    </svg>
  ),
  signalements: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2 L2 22 H22 Z"/>
      <line x1="12" y1="9" x2="12" y2="14"/>
      <circle cx="12" cy="17.5" r="0.5" fill="currentColor"/>
    </svg>
  ),
  cartographie: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 4 L3 6 V20 L9 18 L15 20 L21 18 V4 L15 6 Z"/>
      <line x1="9" y1="4" x2="9" y2="18"/>
      <line x1="15" y1="6" x2="15" y2="20"/>
    </svg>
  ),
  contact: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
    </svg>
  ),
  commune: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 21 V10 L12 3 L21 10 V21 Z"/>
      <path d="M9 21 V14 H15 V21"/>
    </svg>
  ),
  vielocale: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="8" r="3"/>
      <circle cx="17" cy="9" r="2.5"/>
      <path d="M3 20 c0-3 3-5 6-5 s6 2 6 5"/>
      <path d="M14 20 c0-2 2-3 4-3 s3 1 3 3"/>
    </svg>
  ),
};

const SHORTCUTS = [
  { to: "/actualites",    icon: "actualites",   label: "Actualités" },
  { to: "/calendrier",    icon: "calendrier",   label: "Calendrier" },
  { to: "/demarches",     icon: "demarches",    label: "Démarches" },
  { to: "/signalements",  icon: "signalements", label: "Signalements" },
  { to: "/cartographie",  icon: "cartographie", label: "Cartographie" },
  { to: "/contact",       icon: "contact",      label: "Contact" },
  { to: "/commune",       icon: "commune",      label: "La commune" },
  { to: "/vie-locale",    icon: "vielocale",    label: "Vie locale" },
];

/* ============================================================
   Page
   ============================================================ */
export default function Accueil() {
  const params = useParams2();
  const [actus, setActus] = useState([]);
  const bgRef = useRef(null);
  const silRef = useRef(null);
  const shortcutsRef = useRef(null);

  useEffect(() => {
    api.get("/actualites?limit=3").then((d) => setActus(d.items || [])).catch(() => {});
  }, []);

  useHeroParallax(bgRef, silRef);
  useScrollReveal([actus]);

  const lat = params.carte_lat || "48.1097";
  const lon = params.carte_lon || "0.0856";
  const bbox = `${parseFloat(lon) - 0.012},${parseFloat(lat) - 0.008},${parseFloat(lon) + 0.012},${parseFloat(lat) + 0.008}`;

  // Chiffres clés — charges dynamiquement depuis /commune/chiffres  [lot6]
  const [chiffres, setChiffres] = useState(null);
  useEffect(() => {
    api.get("/commune/chiffres").then(setChiffres).catch(() => setChiffres(null));
  }, []);

  // Construit la liste en masquant les valeurs absentes (ex. surface indispo).
  const STATS = [];
  if (chiffres) {
    if (chiffres.population != null)
      STATS.push({ to: chiffres.population, suffix: "", label: "Habitants", link: "/commune" });
    if (chiffres.surface_ha != null)
      STATS.push({ to: chiffres.surface_ha, suffix: " ha", label: "de territoire" });
    if (chiffres.associations != null)
      STATS.push({ to: chiffres.associations, suffix: "", label: "Associations", link: "/vie-locale#associations" });
    if (chiffres.sentiers_km != null)
      STATS.push({ to: chiffres.sentiers_km, suffix: " km", label: "de sentiers", link: "/circuits-randonnee" });
  }

  const scrollToContent = () => {
    shortcutsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="accueil-v3">
      {/* ============ HERO CINÉMATIQUE ============ */}
      <section className="hero-v3">
        {/* Couche 1 : photo avec Ken Burns + parallax */}
        <div className="hero-v3-bg" ref={bgRef}>
          <div
            className="hero-v3-bg-img"
            style={{ backgroundImage: `url(${PUB}/hero-village-sunset.jpg)` }}
          />
        </div>
        {/* Couche 2 : voile de lisibilité */}
        <div className="hero-v3-veil" />
        {/* Couche 3 : silhouette du village (profondeur) */}
        <img
          ref={silRef}
          className="hero-v3-silhouette"
          src={`${PUB}/silhouette-village.png`}
          alt=""
          aria-hidden="true"
        />

        {/* Contenu */}
        <div className="hero-v3-content">
          <div className="hero-v3-eyebrow">
            <span className="hero-v3-eyebrow-line" />
            Commune de la Sarthe · Pays de la Loire
            <span className="hero-v3-eyebrow-line" />
          </div>
          <h1 className="hero-v3-title">
            <span className="hero-v3-line"><span className="hero-v3-line-inner">Bienvenue à</span></span>
            <span className="hero-v3-line"><span className="hero-v3-line-inner hero-v3-line-name">La Chapelle-Saint-Fray</span></span>
          </h1>
          <p className="hero-v3-tagline">
            {params.commune_intro ||
              "Le site officiel de votre commune : actualités, démarches et informations pratiques."}
          </p>
          <div className="hero-v3-ctas">
            <Link to="/demarches" className="hero-v3-cta-primary">
              Mes démarches
              <span className="hero-v3-cta-arrow" aria-hidden="true">→</span>
            </Link>
            <Link to="/signalements" className="hero-v3-cta-secondary">
              Signaler un problème
            </Link>
          </div>
        </div>

        {/* Badge circulaire rotatif */}
        <div className="hero-v3-badge" aria-hidden="true">
          <svg viewBox="0 0 120 120" className="hero-v3-badge-text">
            <defs>
              <path id="hv3-circle" d="M60,60 m-46,0 a46,46 0 1,1 92,0 a46,46 0 1,1 -92,0" />
            </defs>
            <text>
              <textPath href="#hv3-circle">
                LA CHAPELLE-SAINT-FRAY · SARTHE · PAYS DE LA LOIRE ·
              </textPath>
            </text>
          </svg>
          <svg viewBox="0 0 24 24" className="hero-v3-badge-star" fill="currentColor">
            <path d="M12 2l2.4 6.2L21 9l-5 4.4L17.5 21 12 17.3 6.5 21 8 13.4 3 9l6.6-.8z" />
          </svg>
        </div>

        {/* Indicateur de scroll */}
        <button className="hero-v3-scroll" onClick={scrollToContent} aria-label="Découvrir le contenu">
          <span className="hero-v3-scroll-chevron" />
          <span className="hero-v3-scroll-chevron" />
        </button>
      </section>

      {/* ============ RACCOURCIS (chevauchent le hero) ============ */}
      <div className="container" ref={shortcutsRef}>
        <div className="shortcuts-v3">
          {SHORTCUTS.map((s, i) => (
            <Link
              key={s.to}
              to={s.to}
              className="shortcut-v3"
              data-reveal
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <span className="shortcut-v3-icon">{ICONS[s.icon]}</span>
              <span className="shortcut-v3-label">{s.label}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* ============ CHIFFRES CLÉS ============ */}  {/* [lot6] */}
      {STATS.length > 0 && (
      <section className="stats-v3" data-reveal>
        <div className="stats-v3-inner">
          {STATS.map((s, i) => {
            const inner = (
              <>
                <div className="stats-v3-num">
                  <Counter to={s.to} suffix={s.suffix} />
                </div>
                <div className="stats-v3-label">{s.label}</div>
              </>
            );
            return s.link ? (
              <Link className="stats-v3-item stats-v3-item-link" key={i} to={s.link}>
                {inner}
              </Link>
            ) : (
              <div className="stats-v3-item" key={i}>{inner}</div>
            );
          })}
        </div>
      </section>
      )}

      {/* ============ ACTUALITÉS ============ */}
      <div className="container">
        <div className="section-head-v3" data-reveal>
          <h2>Dernières actualités</h2>
          <Link to="/actualites" className="section-more-v3">
            Toutes les actualités <span aria-hidden="true">→</span>
          </Link>
        </div>

        {actus.length === 0 ? (
          <p className="muted">Aucune actualité pour le moment.</p>
        ) : (
          <div className="actu-grid">
            {actus.map((a, i) => (
              <article
                key={a.id}
                className="actu-card actu-card-v3"
                data-reveal
                style={{ transitionDelay: `${i * 110}ms` }}
              >
                <Link to={`/actualites/${a.slug}`} className="actu-card-v3-imgwrap">
                  {a.image_principale ? (
                    <img className="actu-card-img" src={UPLOADS + a.image_principale} alt={a.titre} />
                  ) : (
                    <div className="actu-card-noimg">La Chapelle-Saint-Fray</div>
                  )}
                </Link>
                <div className="actu-card-body">
                  <div>
                    <span className={`badge badge-${a.categorie}`}>
                      {CAT_LABELS[a.categorie] || a.categorie}
                    </span>
                  </div>
                  <h3><Link to={`/actualites/${a.slug}`}>{a.titre}</Link></h3>
                  <div className="actu-card-date">{formatDate(a.date_publication)}</div>
                  {a.chapeau && <p className="actu-card-chapeau">{a.chapeau}</p>}
                  <Link to={`/actualites/${a.slug}`} className="actu-card-more actu-card-more-v3">
                    Lire la suite <span aria-hidden="true">→</span>
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* ============ HORAIRES + MAP ============ */}
        <div className="section-head-v3" data-reveal>
          <h2>Horaires &amp; accès</h2>
        </div>
        <div className="info-grid">
          <div className="card info-card-v3" data-reveal>
            <h3>Horaires d'ouverture</h3>
            <table className="horaires-table">
              <tbody>
                <tr><td>Lundi</td><td>{params.horaires_lundi || "—"}</td></tr>
                <tr><td>Mardi</td><td>{params.horaires_mardi || "—"}</td></tr>
                <tr><td>Mercredi</td><td>{params.horaires_mercredi || "—"}</td></tr>
                <tr><td>Jeudi</td><td>{params.horaires_jeudi || "—"}</td></tr>
                <tr><td>Vendredi</td><td>{params.horaires_vendredi || "—"}</td></tr>
                {params.horaires_samedi && params.horaires_samedi !== "Fermé" && (
                  <tr><td>Samedi</td><td>{params.horaires_samedi}</td></tr>
                )}
              </tbody>
            </table>
            <p className="mt-2">
              {params.mairie_adresse || "1 route du Mans"}<br />
              {(params.mairie_cp || "72240") + " " + (params.mairie_ville || "La Chapelle-Saint-Fray")}<br />
              {params.mairie_telephone && <>Tél. {params.mairie_telephone}</>}
            </p>
          </div>
          <div className="map-wrap map-wrap-v3" data-reveal style={{ transitionDelay: "120ms" }}>
            <iframe
              title="Carte de la mairie"
              src={`https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lon}`}
            />
          </div>
        </div>
      </div>

      {/* ============ BANDEAU CONTACT ============ */}
      <section className="cta-v3" data-reveal>
        <div className="cta-v3-inner">
          <div>
            <h2 className="cta-v3-title">Une question, une démarche&nbsp;?</h2>
            <p className="cta-v3-text">L'équipe municipale vous répond et vous accompagne.</p>
          </div>
          <Link to="/contact" className="cta-v3-btn">
            Contacter la mairie <span aria-hidden="true">→</span>
          </Link>
        </div>
      </section>
    </div>
  );
}
